import React, { useEffect, useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from "@mui/material";
import copy from "copy-to-clipboard";
import { useDispatch } from "react-redux";
import { AlertTriangle, AudioLines, Clock, Cpu, FileText, Mic, Phone, Search, Volume2 } from "lucide-react";
import { ReactComponent as CopyIcon } from "../../../assets/Images/CopyIcon.svg";
import { CDN_BASE } from "../../../hooks/useCdnAuth";
import { CopyAlert } from "../CommonComponents/CommonComponents";
import CustomScrollbars from "../CommonComponents/CustomScrollbars";
import EmptyState from "../Common/EmptyState";
import {
  analyzeCallEventsAction,
  analyzeTranscriptAction,
} from "../ProjectSection/CampaignSection/Store/Campaign.action";
import CallEventsPlayer, {
  CATEGORY_META,
  connectedAudioTime,
} from "./CallEventsPlayer";
import CallLogs from "../Campaigns/CallHistory/CallLogs";
import "../../../assets/css/page/CallEvents.scss";

type CallEvent = {
  event_name: string;
  label: string;
  category: string;
  description?: string;
  event_time?: number | null;
  from_start?: number | null;
  from_connected?: number | null;
  rel_seconds?: number | null;
  delta_seconds?: number | null;
  preview?: string | null;
  data?: Record<string, unknown>;
};

type CallUsageMetrics = {
  asr?: {
    count?: number;
    confidence?: number;
    finals?: number;
    partials?: number;
    provider?: string;
  };
  llm?: {
    tokens_in?: number;
    tokens_out?: number;
    tokens?: number;
    requests?: number;
    tokens_in_per_request?: number;
    tokens_out_per_request?: number;
    tokens_per_request?: number;
    requests_per_second?: number;
  };
  tts?: {
    requests?: number;
    cache_hits?: number;
    cache_misses?: number;
    input_chars?: number;
  };
};

type CallEventsResponse = {
  call_uuid?: string;
  phone?: string;
  count?: number;
  analysis?: {
    ring_time?: number;
    silent_count?: number;
    disconnected_inactivity?: boolean;
    duration?: number;
    duration_from_start?: number;
    duration_from_connected?: number;
    avg_end_to_end_latency?: number | null;
    avg_llm_latency?: number | null;
  };
  metrics?: CallUsageMetrics;
  events?: CallEvent[];
};

const FILTERS = ["all", "lifecycle", "speech", "turn", "asr", "silence"] as const;

const formatRel = (seconds?: number | null) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return "—";
  const value = Number(seconds);
  const sign = value < 0 ? "−" : "";
  const total = Math.abs(value);
  const minutes = Math.floor(total / 60);
  const secs = total - minutes * 60;
  return `${sign}${String(minutes).padStart(2, "0")}:${secs.toFixed(2).padStart(5, "0")}`;
};

const formatCount = (value?: number | null) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Math.round(Number(value)).toLocaleString();
};

const formatPercent = (value?: number | null) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `${(Number(value) * 100).toFixed(1)}%`;
};

const formatRate = (value?: number | null) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  const n = Number(value);
  if (n >= 10) return n.toFixed(1);
  if (n >= 1) return n.toFixed(2);
  return n.toFixed(3);
};

const formatDuration = (seconds?: number | null) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return "--";
  const value = Number(seconds);
  return value >= 60 ? formatRel(value) : `${value.toFixed(2)}s`;
};

const normalizeText = (value?: string | null) =>
  (value || "").replace(/\s+/g, " ").trim().toLowerCase();

const textsMatch = (left?: string | null, right?: string | null) => {
  const a = normalizeText(left);
  const b = normalizeText(right);
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
};

const isUserTurn = (type?: string) => {
  const value = (type || "").toLowerCase();
  return value === "input" || value === "user" || value === "human";
};

const eventSearchText = (event: CallEvent) =>
  [
    event.label,
    event.event_name,
    event.preview,
    event.description,
    event.category,
    event.data ? JSON.stringify(event.data) : "",
  ]
    .join(" ")
    .toLowerCase();

const snippetAround = (text?: string | null, query?: string) => {
  const raw = (text || "").replace(/\s+/g, " ").trim();
  if (!raw) return "";
  const haystack = raw.toLowerCase();
  const needle = (query || "").trim().toLowerCase();
  const at = needle ? haystack.indexOf(needle) : 0;
  if (at < 0) return raw.length > 90 ? `${raw.slice(0, 87)}…` : raw;
  const start = Math.max(0, at - 28);
  const end = Math.min(raw.length, at + needle.length + 42);
  const slice = `${start > 0 ? "…" : ""}${raw.slice(start, end)}${
    end < raw.length ? "…" : ""
  }`;
  return slice;
};

type SearchHit = {
  key: string;
  eventIndex: number;
  event: CallEvent;
  title: string;
  snippet: string;
  fromConnected: number | null;
};

const CallEventsModal = ({ show, onClose, selectedCall }) => {
  const dispatch = useDispatch();
  const itemRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const transcriptRefs = useRef<Record<number, HTMLElement | null>>({});
  const searchBoxRef = useRef<HTMLDivElement | null>(null);
  const [copyAlert, setCopyAlert] = useState({ open: false, message: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchCursor, setSearchCursor] = useState(0);
  const [pendingScroll, setPendingScroll] = useState<number | null>(null);
  const [spin, setSpin] = useState(true);
  const [payload, setPayload] = useState<CallEventsResponse | null>(null);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("all");
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [currentTime, setCurrentTime] = useState(0);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [seekRequest, setSeekRequest] = useState<{ time: number; id: number } | null>(
    null
  );
  const [showLogs, setShowLogs] = useState(false);
  const [transcript, setTranscript] = useState<
    Array<{ type?: string; value?: string }>
  >([]);
  const [transcriptSpin, setTranscriptSpin] = useState(false);
  const [transcriptLoaded, setTranscriptLoaded] = useState(false);

  const callUuid =
    selectedCall?.call_uuid || selectedCall?._id || selectedCall?.uuid;
  // Same as Call History: cookie-gated CDN, not the GCS URL on the document.
  const audioUrl = callUuid ? `${CDN_BASE}/${callUuid}.mp3` : "";

  useEffect(() => {
    let cancelled = false;
    const fetchEvents = async () => {
      if (!callUuid) {
        setSpin(false);
        setPayload({ events: [], count: 0 });
        return;
      }
      setSpin(true);
      setFilter("all");
      setExpanded({});
      setActiveIndex(null);
      setCurrentTime(0);
      setTranscript([]);
      setTranscriptLoaded(false);
      setShowLogs(false);
      setSearchQuery("");
      setSearchOpen(false);
      setSearchCursor(0);
      try {
        const res: any = await dispatch(analyzeCallEventsAction(callUuid) as any);
        if (!cancelled) {
          setPayload(res || { events: [], count: 0 });
        }
      } catch {
        if (!cancelled) {
          setPayload({ events: [], count: 0 });
        }
      } finally {
        if (!cancelled) setSpin(false);
      }
    };

    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, [callUuid, dispatch]);

  const events = payload?.events || [];
  const analysis = payload?.analysis || {};
  const usage = payload?.metrics || {};
  const hasUsage = Boolean(usage.asr || usage.llm || usage.tts);
  const durationHint =
    Number(analysis.duration_from_connected) ||
    Number(selectedCall?.duration) ||
    Number(analysis.duration) ||
    0;
  const visibleEvents = useMemo(() => {
    if (filter === "all") return events;
    return events.filter((event) => event.category === filter);
  }, [events, filter]);

  const counts = useMemo(() => {
    const next: Record<string, number> = { all: events.length };
    events.forEach((event) => {
      next[event.category] = (next[event.category] || 0) + 1;
    });
    return next;
  }, [events]);

  const searchNeedle = searchQuery.trim().toLowerCase();
  const searchHits = useMemo(() => {
    if (!searchNeedle) return [] as SearchHit[];
    const hits: SearchHit[] = [];
    const seen = new Set<number>();

    events.forEach((event, eventIndex) => {
      if (!eventSearchText(event).includes(searchNeedle)) return;
      seen.add(eventIndex);
      hits.push({
        key: `event-${eventIndex}`,
        eventIndex,
        event,
        title: event.label || event.event_name,
        snippet:
          snippetAround(event.preview, searchQuery) ||
          snippetAround(event.description, searchQuery),
        fromConnected: connectedAudioTime(event),
      });
    });

    transcript.forEach((message, tIndex) => {
      if (!normalizeText(message?.value).includes(searchNeedle)) return;
      const eventIndex = events.findIndex((event) =>
        textsMatch(event.preview, message?.value)
      );
      if (eventIndex >= 0 && seen.has(eventIndex)) return;
      const event = eventIndex >= 0 ? events[eventIndex] : null;
      if (eventIndex >= 0) seen.add(eventIndex);
      hits.push({
        key: `transcript-${tIndex}`,
        eventIndex: eventIndex >= 0 ? eventIndex : -1,
        event: event || {
          event_name: "transcript",
          label: isUserTurn(message?.type) ? "User" : "Bot",
          category: "speech",
          preview: message?.value,
        },
        title: isUserTurn(message?.type) ? "User" : "Bot",
        snippet: snippetAround(message?.value, searchQuery),
        fromConnected: event ? connectedAudioTime(event) : null,
      });
    });

    hits.sort((a, b) => {
      const left = a.fromConnected;
      const right = b.fromConnected;
      if (left == null && right == null) return 0;
      if (left == null) return 1;
      if (right == null) return -1;
      return left - right;
    });
    return hits;
  }, [events, searchNeedle, searchQuery, transcript]);

  useEffect(() => {
    setSearchCursor(0);
  }, [searchNeedle]);

  useEffect(() => {
    const onPointer = (event: MouseEvent) => {
      if (!searchBoxRef.current?.contains(event.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, []);

  useEffect(() => {
    if (!callUuid || transcriptLoaded) return;
    let cancelled = false;
    const fetchTranscript = async () => {
      setTranscriptSpin(true);
      try {
        const res: any = await dispatch(analyzeTranscriptAction(callUuid) as any);
        if (!cancelled) {
          setTranscript(
            Array.isArray(res?.transcription) ? res.transcription : []
          );
          setTranscriptLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setTranscript([]);
          setTranscriptLoaded(true);
        }
      } finally {
        if (!cancelled) setTranscriptSpin(false);
      }
    };
    fetchTranscript();
    return () => {
      cancelled = true;
    };
  }, [callUuid, transcriptLoaded, dispatch]);

  const activeTranscriptIndex = useMemo(() => {
    const preview = visibleEvents[activeIndex ?? -1]?.preview;
    if (!preview) return null;
    const exact = transcript.findIndex((message) =>
      textsMatch(message?.value, preview)
    );
    return exact >= 0 ? exact : null;
  }, [activeIndex, transcript, visibleEvents]);

  useEffect(() => {
    if (activeTranscriptIndex == null) return;
    transcriptRefs.current[activeTranscriptIndex]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [activeTranscriptIndex]);

  useEffect(() => {
    if (activeIndex != null) return;
    let latest = -1;
    visibleEvents.forEach((event, index) => {
      const audioTime = connectedAudioTime(event);
      if (audioTime != null && audioTime >= 0 && audioTime <= currentTime + 0.04) {
        latest = index;
      }
    });
    if (latest >= 0) {
      setActiveIndex(latest);
    }
  }, [currentTime, visibleEvents, activeIndex]);

  const seekToEvent = (index: number, scroll = false) => {
    const event = visibleEvents[index];
    const audioTime = connectedAudioTime(event);
    setActiveIndex(index);
    if (audioTime != null && audioTime >= 0) {
      setSeekRequest({ time: audioTime, id: Date.now() });
    }
    if (scroll) {
      itemRefs.current[index]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  };

  const jumpToHit = (hit: SearchHit) => {
    const event = hit.eventIndex >= 0 ? events[hit.eventIndex] : hit.event;
    if (!event) return;
    if (filter !== "all") setFilter("all");
    const audioTime = connectedAudioTime(event);
    if (hit.eventIndex >= 0) {
      setActiveIndex(hit.eventIndex);
      setPendingScroll(hit.eventIndex);
    }
    if (audioTime != null && audioTime >= 0) {
      setSeekRequest({ time: audioTime, id: Date.now() });
    }
    setSearchOpen(false);
  };

  useEffect(() => {
    if (pendingScroll == null || filter !== "all") return;
    const frame = requestAnimationFrame(() => {
      itemRefs.current[pendingScroll]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
      setPendingScroll(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [pendingScroll, filter, visibleEvents]);

  const seekFromTranscript = (text?: string) => {
    if (!text) return;
    const matchIndex = visibleEvents.findIndex((event) =>
      textsMatch(event.preview, text)
    );
    if (matchIndex >= 0) {
      seekToEvent(matchIndex, true);
      return;
    }
    const allIndex = events.findIndex((event) => textsMatch(event.preview, text));
    if (allIndex < 0) return;
    const event = events[allIndex];
    const audioTime = connectedAudioTime(event);
    setFilter("all");
    setActiveIndex(allIndex);
    if (audioTime != null && audioTime >= 0) {
      setSeekRequest({ time: audioTime, id: Date.now() });
    }
  };

  return (
    <Modal
      open={show}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose();
      }}
      disableEscapeKeyDown
    >
      <Box className="events-container is-split" sx={{ maxHeight: "98vh" }}>
        <Box className="flex-between events-header">
          <Box>
            <Typography className="font-14-600-grey">
              Call Events -{" "}
              <Typography component="span" className="font-13-500-grey">
                {selectedCall?.phone ||
                  selectedCall?.phone_number ||
                  payload?.phone ||
                  ""}
              </Typography>
            </Typography>
            <Typography className="font-11-500-blue">
              {callUuid}{" "}
              {callUuid && (
                <Tooltip title="Copy UUID">
                  <IconButton
                    size="small"
                    onClick={() => {
                      copy(callUuid);
                      setCopyAlert({ open: true, message: "UUID copied!" });
                    }}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Typography>
          </Box>
          <Box className="events-header-actions">
            <div className="events-search" ref={searchBoxRef}>
              <Search size={14} className="events-search-icon" />
              <input
                type="search"
                className="events-search-input"
                placeholder="Search events or transcript"
                value={searchQuery}
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => {
                  if (searchQuery.trim()) setSearchOpen(true);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setSearchOpen(false);
                    return;
                  }
                  if (!searchHits.length) return;
                  if (event.key === "ArrowDown") {
                    event.preventDefault();
                    setSearchOpen(true);
                    setSearchCursor((prev) =>
                      Math.min(prev + 1, searchHits.length - 1)
                    );
                  } else if (event.key === "ArrowUp") {
                    event.preventDefault();
                    setSearchCursor((prev) => Math.max(prev - 1, 0));
                  } else if (event.key === "Enter") {
                    event.preventDefault();
                    jumpToHit(searchHits[searchCursor] || searchHits[0]);
                  }
                }}
              />
              {searchNeedle ? (
                <span className="events-search-count">
                  {searchHits.length}
                </span>
              ) : null}
              {searchOpen && searchNeedle ? (
                <div className="events-search-menu">
                  <div className="events-search-menu-head">
                    {searchHits.length}{" "}
                    {searchHits.length === 1 ? "result" : "results"}
                  </div>
                  {searchHits.length === 0 ? (
                    <div className="events-search-empty">No matches</div>
                  ) : (
                    searchHits.map((hit, index) => {
                      const category =
                        CATEGORY_META[hit.event.category] || CATEGORY_META.other;
                      return (
                        <button
                          type="button"
                          key={hit.key}
                          className={`events-search-hit${
                            index === searchCursor ? " is-active" : ""
                          }`}
                          onMouseEnter={() => setSearchCursor(index)}
                          onClick={() => jumpToHit(hit)}
                        >
                          <span className="events-search-hit-main">
                            <span className="events-search-hit-title">
                              {hit.title}
                            </span>
                            {hit.snippet ? (
                              <span className="events-search-hit-snippet">
                                {hit.snippet}
                              </span>
                            ) : null}
                          </span>
                          <span className="events-search-hit-meta">
                            <span className="events-search-hit-time">
                              <span className="events-search-hit-time-label">
                                Conn
                              </span>
                              {hit.fromConnected == null ||
                              hit.fromConnected < 0
                                ? "—"
                                : formatRel(hit.fromConnected)}
                            </span>
                            <span
                              className="events-search-hit-cat"
                              style={{
                                color: category.color,
                                backgroundColor: category.background,
                              }}
                            >
                              {category.label}
                            </span>
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              ) : null}
            </div>
            {callUuid && (
              <button
                type="button"
                className="events-logs-btn"
                onClick={() => setShowLogs(true)}
              >
                <FileText size={14} />
                View logs
              </button>
            )}
            <IconButton
              onClick={onClose}
              sx={{
                color: "#62656E",
                height: 8,
                width: 8,
                p: 1,
                borderRadius: 0.75,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {!spin && (events.length > 0 || hasUsage) && (
          <>
            <Box className="events-stats">
              <span className="events-stat-chip">
                <Clock size={13} />
                From start{" "}
                <strong>
                  {formatDuration(
                    analysis.duration_from_start ?? analysis.duration
                  )}
                </strong>
              </span>
              <span className="events-stat-chip">
                <Phone size={13} />
                From connected{" "}
                <strong>
                  {formatDuration(analysis.duration_from_connected)}
                </strong>
              </span>
              <span className="events-stat-chip">
                Ring time <strong>{formatDuration(analysis.ring_time)}</strong>
              </span>
              {analysis.avg_end_to_end_latency != null && (
                <Tooltip
                  arrow
                  title={
                    <span>
                      Time from the human stopping to the bot starting to speak.
                      <br />
                      {formatDuration(analysis.avg_end_to_end_latency)} means:
                      human stops → about{" "}
                      {formatDuration(analysis.avg_end_to_end_latency)} later
                      the bot starts speaking.
                      <br />
                      Not how long the bot talked.
                    </span>
                  }
                >
                  <span className="events-stat-chip has-tip">
                    <Volume2 size={13} />
                    Avg reply{" "}
                    <strong>
                      {formatDuration(analysis.avg_end_to_end_latency)}
                    </strong>
                  </span>
                </Tooltip>
              )}
              {(analysis.silent_count || 0) > 0 && (
                <span className="events-stat-chip warn">
                  Silent prompts <strong>{analysis.silent_count}</strong>
                </span>
              )}
              {analysis.disconnected_inactivity && (
                <span className="events-stat-chip danger">
                  <AlertTriangle size={13} />
                  Ended for inactivity
                </span>
              )}
              <span className="events-stat-chip">
                Events <strong>{payload?.count || events.length}</strong>
              </span>
              {usage.asr && (
                <Tooltip
                  arrow
                  title={
                    <span>
                      {usage.asr.provider
                        ? `${usage.asr.provider} · `
                        : ""}
                      {formatCount(usage.asr.count)} streaming session
                      {usage.asr.count === 1 ? "" : "s"}
                      {usage.asr.confidence != null
                        ? ` · ${formatPercent(usage.asr.confidence)} avg confidence on finals`
                        : ""}
                      {usage.asr.finals != null
                        ? ` · ${formatCount(usage.asr.finals)} final transcripts`
                        : ""}
                      {usage.asr.partials != null
                        ? ` · ${formatCount(usage.asr.partials)} partials`
                        : ""}
                    </span>
                  }
                >
                  <span className="events-stat-chip has-tip">
                    <Mic size={13} />
                    ASR{" "}
                    <strong>
                      {usage.asr.count != null
                        ? formatCount(usage.asr.count)
                        : formatCount(usage.asr.finals)}
                      {usage.asr.confidence != null
                        ? ` · ${formatPercent(usage.asr.confidence)}`
                        : ""}
                      {usage.asr.finals != null
                        ? ` · ${formatCount(usage.asr.finals)} final`
                        : ""}
                    </strong>
                  </span>
                </Tooltip>
              )}
              {usage.llm && (
                <Tooltip
                  arrow
                  title={
                    <span>
                      Total In {formatCount(usage.llm.tokens_in)} · Out{" "}
                      {formatCount(usage.llm.tokens_out)}
                      {usage.llm.tokens != null
                        ? ` · Combined ${formatCount(usage.llm.tokens)}`
                        : ""}
                      <br />
                      Per request In{" "}
                      {formatCount(usage.llm.tokens_in_per_request)} · Out{" "}
                      {formatCount(usage.llm.tokens_out_per_request)}
                      <br />
                      {formatCount(usage.llm.requests)} requests
                      {usage.llm.requests_per_second != null
                        ? ` · ${formatRate(usage.llm.requests_per_second)} requests per second over connected time`
                        : ""}
                    </span>
                  }
                >
                  <span className="events-stat-chip has-tip">
                    <Cpu size={13} />
                    LLM{" "}
                    <strong>
                      In {formatCount(usage.llm.tokens_in)} · Out{" "}
                      {formatCount(usage.llm.tokens_out)} ·{" "}
                      {formatCount(usage.llm.requests)} req
                      {usage.llm.tokens_in_per_request != null
                        ? ` · ${formatCount(usage.llm.tokens_in_per_request)} in/req`
                        : ""}
                      {usage.llm.tokens_out_per_request != null
                        ? ` · ${formatCount(usage.llm.tokens_out_per_request)} out/req`
                        : ""}
                      {usage.llm.requests_per_second != null
                        ? ` · ${formatRate(usage.llm.requests_per_second)}/s`
                        : ""}
                    </strong>
                  </span>
                </Tooltip>
              )}
              {usage.tts && (
                <Tooltip
                  arrow
                  title={
                    <span>
                      {formatCount(usage.tts.requests)} TTS requests
                      {usage.tts.cache_hits != null
                        ? ` · ${formatCount(usage.tts.cache_hits)} served from cache`
                        : ""}
                      {usage.tts.cache_misses != null
                        ? ` · ${formatCount(usage.tts.cache_misses)} generated`
                        : ""}
                      {usage.tts.input_chars != null
                        ? ` · ${formatCount(usage.tts.input_chars)} characters of text sent (not audio)`
                        : ""}
                    </span>
                  }
                >
                  <span className="events-stat-chip has-tip">
                    <AudioLines size={13} />
                    TTS{" "}
                    <strong>
                      {formatCount(usage.tts.requests)}
                      {usage.tts.cache_hits != null
                        ? ` · ${formatCount(usage.tts.cache_hits)} cache`
                        : ""}
                      {usage.tts.input_chars != null
                        ? ` · ${formatCount(usage.tts.input_chars)} chars`
                        : ""}
                    </strong>
                  </span>
                </Tooltip>
              )}
            </Box>

            {audioUrl && (
              <CallEventsPlayer
                audioUrl={audioUrl}
                callUuid={callUuid}
                events={visibleEvents}
                durationHint={durationHint}
                activeIndex={activeIndex}
                seekRequest={seekRequest}
                onTimeChange={(time) => {
                  setActiveIndex(null);
                  setCurrentTime(time);
                }}
                onMarkerClick={(index) => seekToEvent(index, true)}
              />
            )}

            <Box className="events-filters">
              {FILTERS.map((key) => {
                const meta = CATEGORY_META[key];
                const active = filter === key;
                const count = counts[key] || 0;
                if (key !== "all" && count === 0) return null;
                return (
                  <Chip
                    key={key}
                    label={`${meta.label} ${count}`}
                    size="small"
                    onClick={() => {
                      setFilter(key);
                      setExpanded({});
                      setActiveIndex(null);
                    }}
                    className={`events-filter-chip${active ? " active" : ""}`}
                    sx={{
                      borderColor: `${meta.color} !important`,
                      color: active ? "#fff !important" : `${meta.color} !important`,
                      backgroundColor: active
                        ? `${meta.color} !important`
                        : `${meta.background} !important`,
                    }}
                  />
                );
              })}
            </Box>
          </>
        )}

        <Box className="events-split">
          <CustomScrollbars height={events.length > 0 ? "46vh" : "68vh"}>
            <Box sx={{ height: "100%", px: 3, position: "relative" }}>
              {spin ? (
                <CircularProgress className="circular-progress" />
              ) : visibleEvents.length > 0 ? (
                <Box className="events-timeline">
                  {visibleEvents.map((event, index) => {
                    const category = CATEGORY_META[event.category] || CATEGORY_META.other;
                    const hasDetails =
                      event.data && Object.keys(event.data).length > 0;
                    const isOpen = Boolean(expanded[index]);
                    const isActive = activeIndex === index;
                    return (
                      <Box
                        className={`events-item${isActive ? " is-active" : ""}`}
                        key={`${event.event_name}-${index}`}
                        ref={(node) => {
                          itemRefs.current[index] = node as HTMLDivElement | null;
                        }}
                        onClick={() => seekToEvent(index)}
                      >
                        <Box className="events-item-time">
                          <span className="events-item-time-row">
                            <span className="events-item-time-label">Start</span>
                            {formatRel(event.from_start ?? event.rel_seconds)}
                          </span>
                          <span className="events-item-time-row connected">
                            <span className="events-item-time-label">Conn</span>
                            {event.from_connected == null ||
                            event.from_connected < 0
                              ? "—"
                              : formatRel(event.from_connected)}
                          </span>
                        </Box>
                        <Box className="events-item-rail">
                          <Box
                            className="events-item-dot"
                            sx={{ color: category.color }}
                          />
                        </Box>
                        <Box className="events-item-card">
                          <Box className="events-item-title">
                            <Typography className="events-item-label">
                              {event.label}
                            </Typography>
                            <Box
                              className="events-item-category"
                              sx={{
                                color: category.color,
                                backgroundColor: category.background,
                              }}
                            >
                              {category.label}
                            </Box>
                          </Box>
                          {event.preview && (
                            <Typography className="events-item-preview">
                              {event.preview}
                            </Typography>
                          )}
                          {event.delta_seconds != null && event.delta_seconds > 0 && (
                            <Typography className="events-item-meta">
                              +{event.delta_seconds.toFixed(2)}s from previous
                            </Typography>
                          )}
                          {hasDetails && (
                            <>
                              <button
                                type="button"
                                className="events-item-toggle"
                                onClick={(clickEvent) => {
                                  clickEvent.stopPropagation();
                                  setExpanded((prev) => ({
                                    ...prev,
                                    [index]: !prev[index],
                                  }));
                                }}
                              >
                                {isOpen ? "Hide details" : "Show details"}
                              </button>
                              {isOpen && (
                                <pre className="events-item-details">
                                  {JSON.stringify(event.data, null, 2)}
                                </pre>
                              )}
                            </>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box className="events-empty">
                  <EmptyState message="No Events Found" />
                </Box>
              )}
            </Box>
          </CustomScrollbars>

          <Box className="events-transcript">
            <Box className="events-transcript-head">Transcript</Box>
            <CustomScrollbars height={events.length > 0 ? "46vh" : "68vh"}>
              <Box className="events-transcript-body">
                {transcriptSpin ? (
                  <CircularProgress className="circular-progress" size={22} />
                ) : transcript.length > 0 ? (
                  transcript.map((message, index) => {
                    const userTurn = isUserTurn(message?.type);
                    return (
                      <button
                        type="button"
                        key={`${message?.type}-${index}`}
                        className={`events-transcript-row${
                          userTurn ? " is-user" : " is-bot"
                        }${
                          activeTranscriptIndex === index ? " is-active" : ""
                        }`}
                        ref={(node) => {
                          transcriptRefs.current[index] = node;
                        }}
                        onClick={() => seekFromTranscript(message?.value)}
                      >
                        <span className="events-transcript-who">
                          {userTurn ? "User" : "Bot"}
                        </span>
                        <span className="events-transcript-text">
                          {message?.value || ""}
                        </span>
                      </button>
                    );
                  })
                ) : (
                  <Typography className="events-transcript-empty">
                    No transcript for this call
                  </Typography>
                )}
              </Box>
            </CustomScrollbars>
          </Box>
        </Box>

        {callUuid && (
          <CallLogs
            call_uuid={callUuid}
            show={showLogs}
            toggle={() => setShowLogs(false)}
          />
        )}

        <CopyAlert
          open={copyAlert.open}
          onClose={() => setCopyAlert({ ...copyAlert, open: false })}
          alertMessage={copyAlert.message}
          styles={{ mt: 9 }}
        />
      </Box>
    </Modal>
  );
};

export default CallEventsModal;
